import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import type { allChangelogs } from "content-collections";
import { useState } from "react";
import { ChangeLogCard } from "@/components/cards/change-log-card";
import { ChangelogTypeFilter } from "@/components/misc/ChangeLogFilter";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { config } from "@/data/config";
import { changelogVersionSectionClass } from "@/lib/changelog-type-styles";
import { isModuleEnabled } from "@/lib/modules";
import { cn } from "@/lib/utils";
import { getStaggeredAnimationClasses } from "@/utils/animation";
import {
	changelogsByVersion,
	sortedVersionsNewestFirst,
} from "@/utils/change-log";
import { seo } from "@/utils/seo";

const sortedVersions = sortedVersionsNewestFirst;
const skipVersions = config.changelog.show_last_versions;
const archivedVersions = sortedVersions.slice(skipVersions);

export const Route = createFileRoute("/_marketing/changelog/archive")({
	beforeLoad: () => {
		if (!isModuleEnabled("marketing_changelog")) {
			throw redirect({ to: "/" });
		}
	},
	component: RouteComponent,
	head: () =>
		seo({
			title: "Changelog Archive",
			description:
				"Archive of all previous features, improvements, and bug fixes.",
			keywords:
				"changelog, updates, changes, bug fixes, improvements, features, archive",
		}),
});

export function RouteComponent() {
	const [selectedType, setSelectedType] = useState<string>("all");

	const filteredChangelogs = (changelogs: typeof allChangelogs) => {
		if (selectedType === "all") return changelogs;
		return changelogs.filter((changelog) => changelog.type === selectedType);
	};

	return (
		<MarketingShell>
			<section className="pt-20 pb-16 sm:pt-28 sm:pb-24">
				<MarketingContainer>
					<MarketingHeader
						title="Archive"
						heading="Earlier releases in full detail"
						description="Every older version stays documented here so you can audit what shipped, when, and how it affected your integration."
					/>

					<div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
						<ChangelogTypeFilter
							selectedType={selectedType}
							onSelectType={setSelectedType}
						/>
						<Link
							to="/changelog"
							className="text-sm font-medium text-primary hover:underline"
						>
							← Latest releases
						</Link>
					</div>

					<div className="flex flex-col gap-14 sm:gap-16">
						{archivedVersions.map((version, index) => {
							const filteredVersionChangelogs = filteredChangelogs(
								changelogsByVersion[version] ?? [],
							);
							if (filteredVersionChangelogs.length === 0) return null;

							return (
								<div
									className={cn(
										getStaggeredAnimationClasses(index),
										changelogVersionSectionClass,
									)}
									key={version}
								>
									<h2 className="mb-6 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
										{version}
									</h2>
									<div className="flex flex-col gap-3">
										{filteredVersionChangelogs.map((changelog) => (
											<ChangeLogCard
												key={changelog._meta.path}
												changelog={changelog}
											/>
										))}
									</div>
								</div>
							);
						})}
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
